import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Drawer, Input } from '@/components/ui'
import { useManualPriceEntry } from '../api/queries'

export interface ManualEntryDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Log a price change I heard about" — for the informal-notice detection
 * path (phone call, email mention) with no document to extract from.
 * Feeds the same compare_and_flag pipeline as PO-retroactive detection.
 */
export function ManualEntryDrawer({ open, onOpenChange }: ManualEntryDrawerProps) {
  const [vendorName, setVendorName] = useState('')
  const [productName, setProductName] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [currencyName, setCurrencyName] = useState('')
  const [minQty, setMinQty] = useState('')
  const [packagingName, setPackagingName] = useState('')
  const [notes, setNotes] = useState('')

  const entry = useManualPriceEntry()

  function reset() {
    setVendorName('')
    setProductName('')
    setNewPrice('')
    setCurrencyName('')
    setMinQty('')
    setPackagingName('')
    setNotes('')
  }

  const canSubmit = vendorName.trim() && productName.trim() && Number(newPrice) > 0

  function submit() {
    entry.mutate(
      {
        vendor_name: vendorName.trim(),
        product_name: productName.trim(),
        new_price: Number(newPrice),
        currency_name: currencyName.trim() || undefined,
        min_qty: minQty.trim() ? Number(minQty) : undefined,
        packaging_name: packagingName.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          toast.success(
            data.flag_id
              ? 'Logged — opened a review-queue flag.'
              : 'Logged. No prior price on file to compare against yet, so nothing was flagged.',
          )
          reset()
          onOpenChange(false)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to log the price change.')
        },
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      size="standard"
      title="Log a vendor price change"
      meta="For a phone call or email mention, before it shows up on any PO."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false) }}>
            Cancel
          </Button>
          <Button variant="primary" loading={entry.isPending} disabled={!canSubmit} onClick={submit}>
            Log it
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Vendor</span>
          <Input value={vendorName} onChange={(e) => setVendorName(e.target.value)} placeholder="Vendor name" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Product</span>
          <Input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Product name" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">New price</span>
            <Input type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="0.00" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Currency</span>
            <Input value={currencyName} onChange={(e) => setCurrencyName(e.target.value)} placeholder="Defaults to what's on file" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Applies from qty (optional)</span>
            <Input type="number" value={minQty} onChange={(e) => setMinQty(e.target.value)} placeholder="Matches a tier if known" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Pack size (optional)</span>
            <Input value={packagingName} onChange={(e) => setPackagingName(e.target.value)} placeholder="e.g. 55-gal drum" />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Notes</span>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Who told us, and when it takes effect" />
        </label>
      </div>
    </Drawer>
  )
}
